import React, { useState } from 'react';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

// Helper - darken color
function darkenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) - amount * 255);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount * 255);
  const b = Math.max(0, (num & 0xff) - amount * 255);
  return '#' + ((1 << 24) | (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b)).toString(16).slice(1);
}

/**
 * Structure renderer - creates different tent styles based on structure_type
 * Matches Pelan Susun Atur Tapak of Hari Landskap Negara 2026
 */
export function StructureRenderer({ booth, onSelect, isSelected, weatherCondition }) {
  const [hovered, setHovered] = useState(false);

  const isRaining = ['rainy', 'stormy'].includes(weatherCondition);
  const shelterHighlight = isRaining && booth.has_shelter;

  const baseColor = booth.status === 'available' ? '#95a5a6' : (booth.color || '#3b82f6');
  const roofColor = shelterHighlight ? '#3498db' : (booth.status === 'available' ? '#7f8c8d' : darkenColor(baseColor, 0.2));
  const emissive = hovered || isSelected ? 0.4 : (shelterHighlight ? 0.25 : 0);

  const w = booth.width || 6;
  const d = booth.depth || 6;
  const h = booth.height || 3.5;
  const structureType = booth.structure_type || 'khemah_pempamer';

  const handleClick = (e) => { e.stopPropagation(); onSelect(booth); };
  const handleOver = (e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; };
  const handleOut = () => { setHovered(false); document.body.style.cursor = 'auto'; };

  // Render different structures based on type
  const renderStructure = () => {
    switch (structureType) {
      case 'khemah_utama':
        // Main tent 50x196 ft - HUGE rectangular tent with A-frame roof
        return (
          <group>
            {/* Base platform */}
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#8b7355" roughness={0.8} />
            </mesh>
            {/* Walls */}
            <mesh position={[0, h/2, -d/2]} castShadow>
              <boxGeometry args={[w, h, 0.15]} />
              <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[-w/2, h/2, 0]} castShadow>
              <boxGeometry args={[0.15, h, d]} />
              <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[w/2, h/2, 0]} castShadow>
              <boxGeometry args={[0.15, h, d]} />
              <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            {/* A-frame roof */}
            <mesh position={[0, h + 1.5, 0]} rotation={[0, Math.PI/2, 0]} castShadow>
              <cylinderGeometry args={[d/2 + 0.5, d/2 + 0.5, w + 1, 3, 1, false, 0, Math.PI]} />
              <meshStandardMaterial color="#eab308" emissive={baseColor} emissiveIntensity={emissive} roughness={0.4} />
            </mesh>
            {/* Corner poles */}
            {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
              <mesh key={`pole-${x}-${z}`} position={[x * w/2, h/2, z * d/2]} castShadow>
                <cylinderGeometry args={[0.2, 0.2, h, 6]} />
                <meshStandardMaterial color="#4a4a4a" metalness={0.6} />
              </mesh>
            ))}
            {/* Flag on top */}
            <mesh position={[0, h + 4, 0]} castShadow>
              <cylinderGeometry args={[0.1, 0.1, 3, 6]} />
              <meshStandardMaterial color="#c0c0c0" metalness={0.7} />
            </mesh>
            <mesh position={[1, h + 4, 0]} castShadow>
              <planeGeometry args={[2, 1.2]} />
              <meshStandardMaterial color="#dc2626" side={THREE.DoubleSide} />
            </mesh>
          </group>
        );
      
      case 'khemah_jamuan':
        // Banquet tent 40x40 - Larger tent with dome roof
        return (
          <group>
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#3d3d3d" />
            </mesh>
            {/* Walls */}
            {['-z', 'x', '-x'].map((side) => {
              const positions = {'-z': [0, h/2, -d/2, w, h, 0.1], 'x': [w/2, h/2, 0, 0.1, h, d], '-x': [-w/2, h/2, 0, 0.1, h, d]};
              const p = positions[side];
              return (
                <mesh key={side} position={[p[0], p[1], p[2]]} castShadow>
                  <boxGeometry args={[p[3], p[4], p[5]]} />
                  <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
                </mesh>
              );
            })}
            {/* Dome roof */}
            <mesh position={[0, h, 0]} castShadow>
              <sphereGeometry args={[Math.max(w, d) * 0.6, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} roughness={0.4} />
            </mesh>
          </group>
        );
      
      case 'plot_pbt':
        // Local Authority garden plot - Open landscape plot with plants (no full tent)
        return (
          <group>
            {/* Grass/mulch base */}
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#84cc16" roughness={0.9} />
            </mesh>
            {/* Fence corners */}
            {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
              <mesh key={`fence-${x}-${z}`} position={[x * w/2, 1, z * d/2]} castShadow>
                <cylinderGeometry args={[0.1, 0.1, 2, 6]} />
                <meshStandardMaterial color="#7c5e3c" />
              </mesh>
            ))}
            {/* Plants in the plot */}
            {[[-0.3, 0.3],[0.3, 0.3],[-0.3, -0.3],[0.3, -0.3]].map(([px, pz], i) => (
              <group key={`plant-${px}-${pz}`} position={[px * w, 0.2, pz * d]}>
                <mesh position={[0, 0.7, 0]} castShadow>
                  <cylinderGeometry args={[0.15, 0.2, 1.4, 6]} />
                  <meshStandardMaterial color="#5c4033" />
                </mesh>
                <mesh position={[0, 1.7, 0]} castShadow>
                  <coneGeometry args={[0.8, 1.5, 8]} />
                  <meshStandardMaterial color={['#16a34a', '#22c55e', '#65a30d', '#4ade80'][i]} emissive={baseColor} emissiveIntensity={emissive * 0.2} />
                </mesh>
              </group>
            ))}
            {/* Small info sign */}
            <mesh position={[0, 1.5, d/2]} castShadow>
              <boxGeometry args={[2, 1, 0.05]} />
              <meshStandardMaterial color="#f97316" emissive={baseColor} emissiveIntensity={emissive * 0.4} />
            </mesh>
          </group>
        );
      
      case 'khemah_jualan_tanaman':
        // Plant sales tent - Open-front tent with plant display
        return (
          <group>
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#3d3d3d" />
            </mesh>
            {/* 3 walls */}
            <mesh position={[0, h/2, -d/2]} castShadow>
              <boxGeometry args={[w, h, 0.1]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.85} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[-w/2, h/2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.85} />
            </mesh>
            <mesh position={[w/2, h/2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.85} />
            </mesh>
            {/* Flat sloped roof */}
            <mesh position={[0, h + 0.3, 0]} rotation={[Math.PI / 8, 0, 0]} castShadow>
              <boxGeometry args={[w + 0.5, 0.15, d + 1]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
            </mesh>
            {/* Corner poles */}
            {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
              <mesh key={`p-${x}-${z}`} position={[x * w/2, h/2, z * d/2]} castShadow>
                <cylinderGeometry args={[0.1, 0.1, h, 6]} />
                <meshStandardMaterial color="#2c3e50" metalness={0.5} />
              </mesh>
            ))}
            {/* Plants displayed in front */}
            {[[-0.3, 0.3],[0.3, 0.3]].map(([px, pz]) => (
              <mesh key={`front-${px}-${pz}`} position={[px * w, 0.5, pz * d]} castShadow>
                <sphereGeometry args={[0.4, 8, 8]} />
                <meshStandardMaterial color="#16a34a" />
              </mesh>
            ))}
          </group>
        );
      
      case 'khemah_makan':
        // Food & Drink tent - has small chimney/vent
        return (
          <group>
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#3d3d3d" />
            </mesh>
            <mesh position={[0, h/2, -d/2]} castShadow>
              <boxGeometry args={[w, h, 0.1]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[-w/2, h/2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
            </mesh>
            <mesh position={[w/2, h/2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
            </mesh>
            {/* Pyramid roof */}
            <mesh position={[0, h + 1, 0]} castShadow>
              <coneGeometry args={[Math.max(w, d) * 0.75, 2, 4]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
            </mesh>
            {/* Chimney */}
            <mesh position={[w * 0.3, h + 2, 0]} castShadow>
              <cylinderGeometry args={[0.2, 0.2, 1.5, 6]} />
              <meshStandardMaterial color="#4a4a4a" metalness={0.7} />
            </mesh>
          </group>
        );
      
      case 'my_kiosk':
        // MyKiosk - Small colorful stall (10x10 ft)
        return (
          <group>
            {/* Base */}
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#4a4a4a" />
            </mesh>
            {/* Body */}
            <mesh position={[0, h/2 - 0.3, 0]} castShadow>
              <boxGeometry args={[w, h - 0.6, d]} />
              <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            {/* Awning */}
            <mesh position={[0, h - 0.2, d/2 + 0.3]} rotation={[Math.PI / 6, 0, 0]} castShadow>
              <boxGeometry args={[w + 0.4, 0.1, 1.5]} />
              <meshStandardMaterial color="#dc2626" />
            </mesh>
            {/* Sign */}
            <mesh position={[0, h - 0.3, d/2 + 0.05]} castShadow>
              <boxGeometry args={[w * 0.8, 0.4, 0.05]} />
              <meshBasicMaterial color="#fbbf24" />
            </mesh>
          </group>
        );
      
      case 'holding_vvip':
      case 'khemah_kkm':
      case 'khemah_media':
      case 'khemah_urus_setia':
      case 'khemah_katering':
      case 'khemah_kaji_selidik':
        // Support tents - closed rectangular with flat roof
        return (
          <group>
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#3d3d3d" />
            </mesh>
            <mesh position={[0, h/2, 0]} castShadow>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[0, h + 0.15, 0]} castShadow>
              <boxGeometry args={[w + 0.4, 0.3, d + 0.4]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
            </mesh>
            {/* Door */}
            <mesh position={[0, h/2 - 0.3, d/2 + 0.05]}>
              <boxGeometry args={[1, 1.8, 0.05]} />
              <meshStandardMaterial color="#1e293b" />
            </mesh>
          </group>
        );
      
      case 'pentas_utama':
        // Main stage 50x30 ft - large elevated performance platform with LED backdrop
        return (
          <group>
            {/* Stage floor - large elevated platform */}
            <mesh position={[0, 0.6, 0]} castShadow receiveShadow>
              <boxGeometry args={[w, 1.2, d]} />
              <meshStandardMaterial color="#1e293b" roughness={0.6} />
            </mesh>
            {/* Stage front skirt (branded) */}
            <mesh position={[0, 0.6, d/2 + 0.05]}>
              <boxGeometry args={[w, 1.2, 0.05]} />
              <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={0.4} />
            </mesh>
            {/* LED backdrop wall */}
            <mesh position={[0, h * 1.4, -d/2]} castShadow>
              <boxGeometry args={[w * 1.1, h * 2.4, 0.3]} />
              <meshStandardMaterial color="#0f172a" emissive="#3b82f6" emissiveIntensity={0.35} />
            </mesh>
            {/* Screen inset */}
            <mesh position={[0, h * 1.4, -d/2 + 0.16]}>
              <boxGeometry args={[w * 0.95, h * 2.1, 0.02]} />
              <meshStandardMaterial color="#0ea5e9" emissive="#0ea5e9" emissiveIntensity={emissive + 0.6} />
            </mesh>
            {/* Truss frame around stage */}
            {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, z]) => (
              <mesh key={`truss-${x}-${z}`} position={[x * w / 2, h * 1.4, z * d / 2]} castShadow>
                <boxGeometry args={[0.25, h * 2.8, 0.25]} />
                <meshStandardMaterial color="#4b5563" metalness={0.8} roughness={0.3} />
              </mesh>
            ))}
            {/* Top truss beam */}
            <mesh position={[0, h * 2.8, 0]} castShadow>
              <boxGeometry args={[w + 0.5, 0.3, d + 0.5]} />
              <meshStandardMaterial color="#4b5563" metalness={0.8} />
            </mesh>
            {/* Stage lights */}
            {[-w * 0.3, 0, w * 0.3].map((x) => (
              <mesh key={`lt-${x}`} position={[x, h * 2.6, d / 2]}>
                <sphereGeometry args={[0.2, 8, 8]} />
                <meshStandardMaterial color="#fef3c7" emissive="#fef3c7" emissiveIntensity={1.5} />
              </mesh>
            ))}
            {/* Stairs front */}
            <mesh position={[0, 0.3, d / 2 + 0.6]}>
              <boxGeometry args={[w * 0.4, 0.6, 1]} />
              <meshStandardMaterial color="#334155" />
            </mesh>
          </group>
        );

      case 'food_truck':
        // Food truck / mobile vehicle - box body on wheels with side awning
        return (
          <group>
            {/* Wheels */}
            {[[-w * 0.35, -d * 0.35], [w * 0.35, -d * 0.35], [-w * 0.35, d * 0.35], [w * 0.35, d * 0.35]].map(([wx, wz]) => (
              <mesh key={`wheel-${wx}-${wz}`} position={[wx, 0.4, wz]} rotation={[0, 0, Math.PI / 2]} castShadow>
                <cylinderGeometry args={[0.4, 0.4, 0.3, 12]} />
                <meshStandardMaterial color="#111827" roughness={0.9} />
              </mesh>
            ))}
            {/* Truck body */}
            <mesh position={[0, h / 2 + 0.4, 0]} castShadow>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} roughness={0.5} />
            </mesh>
            {/* Cab (front) */}
            <mesh position={[0, h / 2 + 0.4, d / 2 + 0.5]} castShadow>
              <boxGeometry args={[w * 0.9, h * 0.75, 1]} />
              <meshStandardMaterial color={darkenColor(baseColor, 0.15)} />
            </mesh>
            {/* Windshield */}
            <mesh position={[0, h * 0.75 + 0.4, d / 2 + 1.01]}>
              <boxGeometry args={[w * 0.75, h * 0.35, 0.05]} />
              <meshStandardMaterial color="#0ea5e9" transparent opacity={0.55} metalness={0.6} />
            </mesh>
            {/* Serving window (side) */}
            <mesh position={[w / 2 + 0.02, h * 0.55 + 0.4, 0]}>
              <boxGeometry args={[0.05, h * 0.35, d * 0.55]} />
              <meshStandardMaterial color="#0f172a" />
            </mesh>
            {/* Serving counter */}
            <mesh position={[w / 2 + 0.5, h * 0.32 + 0.4, 0]}>
              <boxGeometry args={[1, 0.1, d * 0.55]} />
              <meshStandardMaterial color="#e2e8f0" />
            </mesh>
            {/* Awning */}
            <mesh position={[w / 2 + 0.7, h * 0.8 + 0.4, 0]} rotation={[0, 0, -Math.PI / 8]} castShadow>
              <boxGeometry args={[1.5, 0.05, d * 0.6]} />
              <meshStandardMaterial color="#dc2626" side={THREE.DoubleSide} />
            </mesh>
            {/* Roof sign */}
            <mesh position={[0, h + 0.65, 0]} castShadow>
              <boxGeometry args={[w * 0.6, 0.7, 0.1]} />
              <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.4} />
            </mesh>
          </group>
        );

      case 'supa_dupa':
        // Kids playground - inflatable bouncy castle style
        return (
          <group>
            {/* Ground mat */}
            <mesh position={[0, 0.05, 0]} receiveShadow>
              <boxGeometry args={[w, 0.1, d]} />
              <meshStandardMaterial color="#166534" roughness={0.9} />
            </mesh>
            {/* Bounce base (rounded rectangle) */}
            <mesh position={[0, h * 0.35, 0]} castShadow>
              <boxGeometry args={[w * 0.95, h * 0.7, d * 0.95]} />
              <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} roughness={0.4} />
            </mesh>
            {/* Bouncy towers - 4 corners */}
            {[[-1, -1, '#ef4444'], [1, -1, '#3b82f6'], [-1, 1, '#22c55e'], [1, 1, '#a855f7']].map(([x, z, c]) => (
              <mesh key={`t-${x}-${z}`} position={[x * w * 0.35, h + 0.4, z * d * 0.35]} castShadow>
                <cylinderGeometry args={[w * 0.15, w * 0.18, h * 1.2, 8]} />
                <meshStandardMaterial color={c} roughness={0.4} />
              </mesh>
            ))}
            {/* Tower tops (pointed) */}
            {[[-1, -1, '#fbbf24'], [1, -1, '#fbbf24'], [-1, 1, '#fbbf24'], [1, 1, '#fbbf24']].map(([x, z, c]) => (
              <mesh key={`tt-${x}-${z}`} position={[x * w * 0.35, h * 1.6 + 0.4, z * d * 0.35]} castShadow>
                <coneGeometry args={[w * 0.2, h * 0.8, 8]} />
                <meshStandardMaterial color={c} />
              </mesh>
            ))}
            {/* Front archway / entrance */}
            <mesh position={[0, h * 0.9, d / 2]} rotation={[0, 0, 0]}>
              <torusGeometry args={[w * 0.25, 0.25, 8, 16, Math.PI]} />
              <meshStandardMaterial color="#f59e0b" />
            </mesh>
            {/* Flags on top */}
            {[-1, 0, 1].map((x) => (
              <mesh key={`f-${x}`} position={[x * w * 0.25, h * 2 + 0.5, 0]}>
                <planeGeometry args={[0.6, 0.4]} />
                <meshStandardMaterial color={['#ef4444', '#22c55e', '#3b82f6'][x + 1]} side={THREE.DoubleSide} />
              </mesh>
            ))}
          </group>
        );

      case 'khemah_rahmah':
        // RAHMAH Madani discount tent - yellow banner tent with big front sign
        return (
          <group>
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#3d3d3d" />
            </mesh>
            {/* Walls (3 closed) */}
            <mesh position={[0, h / 2, -d / 2]} castShadow>
              <boxGeometry args={[w, h, 0.1]} />
              <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[-w / 2, h / 2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color="#fef3c7" />
            </mesh>
            <mesh position={[w / 2, h / 2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color="#fef3c7" />
            </mesh>
            {/* Big yellow banner front (RAHMAH) */}
            <mesh position={[0, h + 0.5, d / 2 - 0.05]} castShadow>
              <boxGeometry args={[w, 1.4, 0.15]} />
              <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.4} />
            </mesh>
            <Html position={[0, h + 0.5, d / 2 + 0.05]} center distanceFactor={20} occlude={false}>
              <div className="text-black font-black text-xs tracking-widest pointer-events-none select-none">
                RAHMAH MADANI
              </div>
            </Html>
            {/* Sloped roof */}
            <mesh position={[0, h + 0.3, 0]} rotation={[Math.PI / 12, 0, 0]} castShadow>
              <boxGeometry args={[w + 0.4, 0.15, d + 1]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
            </mesh>
            {/* Corner poles */}
            {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, z]) => (
              <mesh key={`rp-${x}-${z}`} position={[x * w / 2, h / 2, z * d / 2]} castShadow>
                <cylinderGeometry args={[0.1, 0.1, h, 6]} />
                <meshStandardMaterial color="#78350f" />
              </mesh>
            ))}
            {/* Product tables inside */}
            <mesh position={[0, 0.6, 0]}>
              <boxGeometry args={[w * 0.75, 0.1, d * 0.35]} />
              <meshStandardMaterial color="#ea580c" />
            </mesh>
          </group>
        );

      case 'small_booth':
        // Small kiosk booth (10x10 ft) - compact single-color hut
        return (
          <group>
            {/* Base */}
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#4a4a4a" />
            </mesh>
            {/* Walls */}
            <mesh position={[0, h / 2, -d / 2]} castShadow>
              <boxGeometry args={[w, h, 0.1]} />
              <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[-w / 2, h / 2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} />
            </mesh>
            <mesh position={[w / 2, h / 2, 0]} castShadow>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} />
            </mesh>
            {/* Flat roof with slight overhang */}
            <mesh position={[0, h + 0.15, 0]} castShadow>
              <boxGeometry args={[w + 0.3, 0.2, d + 0.3]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive * 0.4} />
            </mesh>
            {/* Front counter opening */}
            <mesh position={[0, h * 0.35, d / 2 - 0.05]}>
              <boxGeometry args={[w * 0.7, 0.1, 0.1]} />
              <meshStandardMaterial color="#e2e8f0" />
            </mesh>
            {/* Header sign */}
            <mesh position={[0, h * 0.85, d / 2]}>
              <boxGeometry args={[w * 0.85, 0.35, 0.05]} />
              <meshStandardMaterial color="#fbbf24" />
            </mesh>
          </group>
        );

      case 'khemah_pempamer':
      default:
        // Standard exhibitor tent - pyramid roof (default)
        return (
          <group>
            <mesh position={[0, 0.1, 0]} receiveShadow>
              <boxGeometry args={[w, 0.2, d]} />
              <meshStandardMaterial color="#3d3d3d" />
            </mesh>
            {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
              <mesh key={`d-${x}-${z}`} position={[x * w/2, h/2, z * d/2]} castShadow>
                <cylinderGeometry args={[0.1, 0.1, h, 6]} />
                <meshStandardMaterial color="#2c3e50" metalness={0.5} />
              </mesh>
            ))}
            <mesh position={[0, h/2, -d/2 + 0.05]}>
              <boxGeometry args={[w, h, 0.1]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
            </mesh>
            <mesh position={[-w/2 + 0.05, h/2, 0]}>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
            </mesh>
            <mesh position={[w/2 - 0.05, h/2, 0]}>
              <boxGeometry args={[0.1, h, d]} />
              <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
            </mesh>
            <mesh position={[0, h + 1, 0]} castShadow>
              <coneGeometry args={[Math.max(w, d) * 0.75, 2, 4]} />
              <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
            </mesh>
          </group>
        );
    }
  };

  return (
    <group
      position={[booth.position_x, 0, booth.position_z]}
      rotation={[0, (booth.rotation || 0) * Math.PI / 180, 0]}
      onClick={handleClick}
      onPointerOver={handleOver}
      onPointerOut={handleOut}
    >
      {renderStructure()}
      
      {/* Booth number label */}
      <Html position={[0, h + 3, 0]} center distanceFactor={40} occlude={false}>
        <div className="pointer-events-none px-2 py-0.5 bg-white text-slate-900 font-bold text-xs rounded shadow whitespace-nowrap"
          style={{ 
            transform: `scale(${hovered || isSelected ? 1.3 : 1})`,
            transition: 'transform 0.2s',
            boxShadow: isSelected ? '0 0 20px rgba(243, 156, 18, 0.9)' : ''
          }}>
          {booth.booth_number}
        </div>
      </Html>

      {/* Selection ring */}
      {isSelected && (
        <mesh position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
          <ringGeometry args={[Math.max(w, d) * 0.6, Math.max(w, d) * 0.65, 32]} />
          <meshBasicMaterial color="#f39c12" transparent opacity={0.7} />
        </mesh>
      )}
      
      {/* Shelter highlight when raining */}
      {shelterHighlight && !isSelected && (
        <mesh position={[0, 0.03, 0]} rotation={[-Math.PI/2, 0, 0]}>
          <ringGeometry args={[Math.max(w, d) * 0.65, Math.max(w, d) * 0.72, 32]} />
          <meshBasicMaterial color="#3498db" transparent opacity={0.4} />
        </mesh>
      )}

      {/* Company floating label when selected */}
      {isSelected && booth.company && (
        <Html position={[0, h + 5, 0]} center>
          <div className="bg-slate-900/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-2xl border border-orange-500/50 whitespace-nowrap pointer-events-none">
            <div className="text-white font-bold text-xs">{booth.company.name}</div>
            <div className="text-orange-400 text-[10px]">{booth.category?.name}</div>
          </div>
        </Html>
      )}
    </group>
  );
}

/**
 * Facility Marker - 3D icons for toilets, prayer rooms, food, parking, etc.
 */
export function FacilityMarker({ facility }) {
  const [hovered, setHovered] = useState(false);
  
  const iconColors = {
    toilet: '#06b6d4',
    toilet_vvip: '#a855f7',
    prayer_room: '#16a34a',
    first_aid: '#dc2626',
    parking: '#3b82f6',
    entrance: '#eab308',
    info: '#8b5cf6',
    food: '#f97316',
  };
  
  const color = iconColors[facility.type] || '#64748b';
  
  return (
    <group
      position={[facility.position_x, 0, facility.position_z]}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; }}
      onPointerOut={() => { setHovered(false); document.body.style.cursor = 'auto'; }}
    >
      {/* Base circle */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <cylinderGeometry args={[1.2, 1.2, 0.1, 16]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={hovered ? 0.6 : 0.3} />
      </mesh>
      
      {/* Vertical marker post */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.08, 3, 6]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.6} />
      </mesh>
      
      {/* Icon plate */}
      <mesh position={[0, 3.2, 0]} castShadow>
        <boxGeometry args={[1.5, 1.5, 0.15]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={hovered ? 0.5 : 0.2} />
      </mesh>
      
      {/* HTML icon overlay */}
      <Html position={[0, 3.2, 0.15]} center distanceFactor={20}>
        <div 
          className="text-3xl pointer-events-none select-none"
          style={{ 
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
            transform: `scale(${hovered ? 1.3 : 1})`,
            transition: 'transform 0.2s'
          }}
        >
          {facility.icon || '📍'}
        </div>
      </Html>
      
      {/* Name label when hovered */}
      {hovered && (
        <Html position={[0, 4.5, 0]} center>
          <div className="bg-slate-900/95 backdrop-blur-sm px-2 py-1 rounded shadow-xl border border-slate-700 whitespace-nowrap pointer-events-none">
            <div className="text-white text-xs font-semibold">{facility.name}</div>
          </div>
        </Html>
      )}
    </group>
  );
}
